/**
 * RegionManager — manages multi-region Firebase app instances for the web app.
 *
 * - Auth always stays on the central (default) Firebase app
 * - Each region gets a secondary Firebase app for Firestore/Storage access
 * - Regional Firebase configs are fetched from projectRegistry on first access
 * - Backend URLs are derived from regions.json (no network call needed)
 */
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import regionsData from '../regions.json';

const centralCode = regionsData.central.countryCode; // "AU"
const regionalApps = {};   // countryCode -> firebase.app.App
const regionalConfigs = {}; // countryCode -> firebase config object
let configFetchPromises = {}; // countryCode -> Promise (dedup concurrent fetches)

/**
 * All regions from regions.json
 */
export const getRegions = () => regionsData.regions;

/**
 * Region info for a specific country
 */
export const getRegionInfo = (countryCode) => regionsData.regions[countryCode];

/**
 * The central/default Firebase app
 */
export const getCentralApp = () => firebase.app();

/**
 * Build the backend URL for a region
 */
export const getBackendUrl = (countryCode) => {
  const r = regionsData.regions[countryCode];
  if (!r) return null;
  return `https://${r.gcpRegion}-${r.projectId}.cloudfunctions.net`;
};

/**
 * Fetch the Firebase config for a regional project from projectRegistry
 */
const fetchRegionalConfig = async (countryCode) => {
  // Check sessionStorage cache first
  const cached = sessionStorage.getItem(`piatto_region_config_${countryCode}`);
  if (cached) {
    const config = JSON.parse(cached);
    regionalConfigs[countryCode] = config;
    return config;
  }

  // Fetch from central Firestore
  const db = firebase.firestore();
  const doc = await db.collection('projectRegistry').doc(countryCode).get();
  if (!doc.exists) {
    throw new Error(`Region ${countryCode} not found in projectRegistry`);
  }

  const config = doc.data();
  regionalConfigs[countryCode] = config;
  sessionStorage.setItem(`piatto_region_config_${countryCode}`, JSON.stringify(config));
  return config;
};

/**
 * Initialize a secondary Firebase app for a region
 */
const initRegionalApp = async (countryCode) => {
  if (regionalApps[countryCode]) return regionalApps[countryCode];

  // Dedup concurrent init calls for the same region
  if (configFetchPromises[countryCode]) {
    await configFetchPromises[countryCode];
    return regionalApps[countryCode];
  }

  const promise = (async () => {
    const config = await fetchRegionalConfig(countryCode);

    const appName = `piatto-${countryCode.toLowerCase()}`;
    let app;
    try {
      app = firebase.app(appName);
    } catch {
      app = firebase.initializeApp({
        apiKey: config.apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        storageBucket: config.storageBucket,
        messagingSenderId: config.messagingSenderId,
        appId: config.appId,
      }, appName);
    }

    // Authenticate with cross-project token
    try {
      const generateToken = firebase.functions().httpsCallable('generateRegionalToken');
      const result = await generateToken({ countryCode });
      await app.auth().signInWithCustomToken(result.data.token);
    } catch (err) {
      console.warn(`Cross-project auth for ${countryCode} failed:`, err.message);
      // Admin may still have direct access via service account rules
    }

    regionalApps[countryCode] = app;
  })();

  configFetchPromises[countryCode] = promise;
  await promise;
  delete configFetchPromises[countryCode];
  return regionalApps[countryCode];
};

/**
 * Get the Firestore instance for a specific region.
 * For the central region (AU), returns the default Firestore.
 * For other regions, initializes a secondary app if needed.
 */
export const getFirestoreForRegion = async (countryCode) => {
  if (countryCode === centralCode) {
    return firebase.firestore();
  }
  const app = await initRegionalApp(countryCode);
  return app.firestore();
};

/**
 * Get the Storage instance for a specific region.
 */
export const getStorageForRegion = async (countryCode) => {
  if (countryCode === centralCode) {
    return firebase.storage();
  }
  const app = await initRegionalApp(countryCode);
  return app.storage();
};

/**
 * Get active (deployed) regions — those that have a projectRegistry entry.
 * Fetches from central Firestore and caches the result.
 */
let activeRegionsCache = null;

export const getActiveRegions = async () => {
  if (activeRegionsCache) return activeRegionsCache;

  try {
    const db = firebase.firestore();
    const snapshot = await db.collection('projectRegistry').get();
    const active = snapshot.docs.map(doc => doc.id);
    // Always include AU (central) even if it's not in projectRegistry
    if (!active.includes(centralCode)) active.unshift(centralCode);
    activeRegionsCache = active;
    return active;
  } catch {
    // Fallback to just AU if we can't read projectRegistry
    activeRegionsCache = [centralCode];
    return activeRegionsCache;
  }
};

/**
 * Clear cached data (e.g. on logout)
 */
export const clearRegionCache = () => {
  activeRegionsCache = null;
  Object.keys(regionalApps).forEach(code => {
    try { regionalApps[code].delete(); } catch {}
    delete regionalApps[code];
  });
  Object.keys(regionalConfigs).forEach(code => {
    sessionStorage.removeItem(`piatto_region_config_${code}`);
    delete regionalConfigs[code];
  });
};
